import { useEffect, useState, RefObject, useCallback } from "react";
import {
    AddChatsHistoryAction,
    OnReadMessagesSuccesAction,
} from "src/app/chats-panel/~store/actions/interfaces";
import { IChatItem } from "src/app/chats-panel/~store/models/ChatItem";
import {
    IMessageItem,
    MessageType,
} from "src/app/chats-panel/~store/models/MessageItem";
import { IChatHistoryContainer } from "src/app/chats-panel/~store/state/ChatItemsState";
import { AppThunkAction } from "src/~store/models/AppThunkAction";

export interface UseChatMessagesScrollProps {
    chatHistoryContainer: IChatHistoryContainer;
    selectedChatId: number;
    isReceivingChat: boolean;
    messages: IMessageItem[];
    chatEl: RefObject<HTMLDivElement>;
    operatorId: number;
    selectedChat?: IChatItem;
    searchChatHistoryByMessenger: boolean;
    onAddChatHistory: (
        chatIdStart: number,
        chatsIdUp: number,
        chatsIdDown: number,
        direction: "up" | "down" | "both"
    ) => AppThunkAction<AddChatsHistoryAction>;
    onReadMessages: (
        messagesId: number[]
    ) => AppThunkAction<OnReadMessagesSuccesAction>;
}

export interface UseChatMessagesScroll {
    isScrollToUpVisible: boolean;
    isScrollToBottomVisible: boolean;
    chatScroll: () => void;
    calculateSpaceToUpAndToBottom: () => void;
    scrollToBottomClicked: () => void;
    scrollToUpClicked: () => void;
}

const useChatMessagesScroll = ({
    chatEl,
    chatHistoryContainer,
    selectedChatId,
    isReceivingChat,
    operatorId,
    selectedChat,
    messages,
    searchChatHistoryByMessenger,
    onAddChatHistory,
    onReadMessages,
}: UseChatMessagesScrollProps): UseChatMessagesScroll => {
    const [isScrollToUpVisible, setIsScrollToUpVisible] = useState(false);
    const [isScrollToBottomVisible, setIsScrollToBottomVisible] =
        useState(false);
    const [initialMessagesLoaded, setInitialMessagesLoaded] = useState(false);
    const [scrollReachedOnce, setScrollReachedOnce] = useState(false);
    const [lastMessageAfterScroll, setLastMessageAfterScroll] = useState<
        IMessageItem | undefined
    >(undefined);
    const [previousScrollDirection, setPreviousScrollDirection] = useState<
        "down" | "up" | undefined
    >();
    const [updatedFromTheApi, setUpdatedFromTheApi] = useState(false);

    const calculateSpaceToUpAndToBottom = useCallback(() => {
        const MIN_HEIGHT = 50;
        if (chatEl.current) {
            if (
                chatEl.current.scrollHeight - chatEl.current.scrollTop >=
                chatEl.current.clientHeight + MIN_HEIGHT
            ) {
                setIsScrollToBottomVisible(true);
            } else {
                setIsScrollToBottomVisible(false);
            }
            if (chatEl.current.scrollTop >= MIN_HEIGHT) {
                setIsScrollToUpVisible(true);
            } else {
                setIsScrollToUpVisible(false);
            }
        }
    }, [chatEl]);

    const addChatHistory = useCallback(
        (direction: "up" | "down" | "both") => {
            if (chatHistoryContainer.isReceivingHistory || !selectedChatId) {
                return;
            }

            if (direction === "both") {
                onAddChatHistory(
                    selectedChatId,
                    chatHistoryContainer.gotBefore + 1,
                    chatHistoryContainer.gotAfter + 1,
                    direction
                );
                setLastMessageAfterScroll(undefined);
                setUpdatedFromTheApi(true);
            } else if (
                direction === "up" &&
                chatHistoryContainer.canGetMoreBefore
            ) {
                setLastMessageAfterScroll(messages?.[0]);
                onAddChatHistory(
                    selectedChatId,
                    chatHistoryContainer.gotBefore + 1,
                    chatHistoryContainer.gotAfter,
                    direction
                );
                setUpdatedFromTheApi(true);
            } else if (
                direction === "down" &&
                chatHistoryContainer.canGetMoreAfter &&
                selectedChat?.isClosed
            ) {
                setLastMessageAfterScroll(messages?.at(-1));
                onAddChatHistory(
                    selectedChatId,
                    chatHistoryContainer.gotBefore,
                    chatHistoryContainer.gotAfter + 1,
                    direction
                );
                setUpdatedFromTheApi(true);
            }
        },
        [
            chatHistoryContainer.canGetMoreAfter,
            chatHistoryContainer.canGetMoreBefore,
            chatHistoryContainer.gotAfter,
            chatHistoryContainer.gotBefore,
            chatHistoryContainer.isReceivingHistory,
            messages,
            onAddChatHistory,
            selectedChat?.isClosed,
            selectedChatId,
        ]
    );

    const chatScroll = useCallback(() => {
        const hasScroll = chatEl?.current
            ? chatEl.current.scrollHeight > chatEl.current.clientHeight
            : false;

        if (!hasScroll) {
            return;
        }

        if (!scrollReachedOnce) {
            setScrollReachedOnce(true);
            return;
        }
        calculateSpaceToUpAndToBottom();
        if (
            chatEl.current?.scrollTop === 0 &&
            chatHistoryContainer.canGetMoreBefore
        ) {
            addChatHistory("up");
            setPreviousScrollDirection("up");
        } else if (
            chatEl.current &&
            chatEl.current?.scrollHeight - chatEl.current?.scrollTop - 1 <=
                chatEl.current?.clientHeight &&
            chatHistoryContainer.canGetMoreAfter
        ) {
            addChatHistory("down");
            setPreviousScrollDirection("down");
        }
    }, [
        addChatHistory,
        calculateSpaceToUpAndToBottom,
        chatEl,
        chatHistoryContainer.canGetMoreAfter,
        chatHistoryContainer.canGetMoreBefore,
        scrollReachedOnce,
    ]);

    const scrollToBottomClicked = useCallback(() => {
        chatEl.current?.scrollTo(
            0,
            chatEl.current?.scrollHeight - chatEl.current.clientHeight - 2
        );
    }, [chatEl]);

    const scrollToUpClicked = useCallback(() => {
        chatEl.current?.scrollTo(0, 1);
    }, [chatEl]);

    const handleInitialScroll = useCallback(() => {
        if (selectedChat?.isClosed) {
            // scroll to first message (initial chatId)
            const firstMessage = messages?.find(
                (message) => selectedChatId === message.chatId
            );
            const firstMessageElement = document.getElementById(
                `message-${firstMessage?.messageId}`
            );
            firstMessageElement?.scrollIntoView(true);
        } else if (operatorId === selectedChat?.operatorId) {
            // scroll to first unread message
            const firstUnreadMessageIndex = messages.findIndex(
                (message) =>
                    message.readTimestamp === null &&
                    message.messageType === MessageType.clientMessage
            );
            const firstUnreadMessage = messages[firstUnreadMessageIndex - 1];
            if (firstUnreadMessage) {
                const firstUnreadMessageElement = document.getElementById(
                    `message-${firstUnreadMessage.messageId}`
                );
                firstUnreadMessageElement?.scrollIntoView(true);
                onReadMessages([firstUnreadMessage.messageId]);
            } else {
                scrollToBottomClicked();
            }
        } else {
            // scroll to bottom
            scrollToBottomClicked();
        }

        if (
            chatEl.current &&
            chatEl.current?.scrollHeight -
                chatEl.current?.scrollTop -
                chatEl.current?.clientHeight <
                1
        ) {
            chatEl.current.scrollTop = chatEl.current.scrollTop - 2;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        messages,
        onReadMessages,
        operatorId,
        scrollToBottomClicked,
        selectedChat?.isClosed,
        selectedChat?.operatorId,
        selectedChatId,
    ]);

    useEffect(() => {
        if (selectedChatId) {
            setInitialMessagesLoaded(false);
        }
    }, [selectedChatId]);

    useEffect(() => {
        if (initialMessagesLoaded) {
            setLastMessageAfterScroll(undefined);
            setUpdatedFromTheApi(false);
        }
    }, [initialMessagesLoaded]);

    useEffect(() => {
        if (
            initialMessagesLoaded ||
            !selectedChatId ||
            chatHistoryContainer.isReceivingHistory ||
            isReceivingChat
        ) {
            return;
        }

        const hasScroll = chatEl?.current
            ? chatEl.current.scrollHeight > chatEl.current.clientHeight
            : false;

        if (
            !hasScroll &&
            (chatHistoryContainer.canGetMoreAfter ||
                chatHistoryContainer.canGetMoreBefore)
        ) {
            if (
                chatHistoryContainer.canGetMoreAfter &&
                chatHistoryContainer.canGetMoreBefore
            ) {
                addChatHistory("both");
            } else if (chatHistoryContainer.canGetMoreAfter) {
                addChatHistory("down");
            } else if (chatHistoryContainer.canGetMoreBefore) {
                addChatHistory("up");
            }
            return;
        } else if (!initialMessagesLoaded && hasScroll) {
            setInitialMessagesLoaded(true);
            handleInitialScroll();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        initialMessagesLoaded,
        selectedChatId,
        chatHistoryContainer.isReceivingHistory,
    ]);

    useEffect(() => {
        setInitialMessagesLoaded(false);
    }, [searchChatHistoryByMessenger]);

    useEffect(() => {
        if (
            messages.length &&
            !isReceivingChat &&
            !chatHistoryContainer.isReceivingHistory &&
            initialMessagesLoaded
        ) {
            const lastMessage = document.getElementById(
                "message-" + lastMessageAfterScroll?.messageId
            );

            if (updatedFromTheApi) {
                if (lastMessageAfterScroll && lastMessage) {
                    lastMessage?.scrollIntoView(
                        previousScrollDirection === "up"
                    );
                    setUpdatedFromTheApi(false);
                    setLastMessageAfterScroll(undefined);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages.length]);

    return {
        isScrollToBottomVisible,
        isScrollToUpVisible,
        chatScroll,
        calculateSpaceToUpAndToBottom,
        scrollToBottomClicked,
        scrollToUpClicked,
    };
};

export default useChatMessagesScroll;
